/**
 * @file Resale-value formula — mirrors the pricing tables IT ran by hand.
 *
 * Two shapes, selected purely by the per-category coefficients (both live in
 * /config/resaleFormulas, editable from Settings):
 *
 * LINEAR (laptops — gracePeriodMonths 0 / sellableAtPercent 100 or unset):
 *   value(m) = initial - (initial - residual) * m / depreciationMonths
 *   clamped to [residual, initial].
 *
 * GRACE + CLIFF (phones — per the old phones sheet):
 *   m < grace                → full purchase price. STRV doesn't sell the
 *                              phone at all in this window (the sheet's red
 *                              rows); the table shows full price, so we do too.
 *   m = grace                → sellableAtPercent of purchase price (75% —
 *                              the immediate second-hand haircut; a whole-
 *                              timeline line would put month 6 at ~5/6, but
 *                              phones lose value faster than that up front).
 *   grace ≤ m < deprMonths   → linear from that point down to the residual
 *                              over the REMAINING (deprMonths - grace) months.
 *   m ≥ deprMonths           → residual floor.
 *
 * Worked example (the sheet, verified row-for-row): 23 880 phone, grace 6,
 * 75%, 36 months, residual 6 000 → months 0-5: 23 880; month 6: 17 910;
 * then -397/month; month 19: 12 749; month 36+: 6 000.
 */

import { CATEGORIES } from './config.js';

import { monthsBetween } from './utils.js';

export function defaultFormulas() {
  return {
    laptop: {
      label: 'Laptop',
      depreciationMonths: 48,
      residualValue: 13000,
      // Optional per-model residual overrides. Keys are substrings to match
      // against the device name (case-insensitive). First match wins.
      modelOverrides: [
        { match: 'macbook pro 13', residualValue: 10000 },
        { match: 'macbook air',    residualValue:  8000 },
      ],
    },
    phone: {
      label: 'Phone',
      depreciationMonths: 36,
      residualValue: 6000,
      // First 6 months: not sold, valued at full price. At month 6 the value
      // cliffs to 75% and depreciates linearly from there (see file header).
      gracePeriodMonths: 6,
      sellableAtPercent: 75,
      modelOverrides: [],
    },
  };
}

/**
 * @param device {{category, name?, purchaseDate?, purchasePrice?}}
 * @param formulas as returned by defaultFormulas() / Firestore /config/resaleFormulas
 * @param asOf optional Date to compute "as of" — defaults to today. Used by
 *   the device-detail forecast to project values into the next few months.
 * @returns CZK integer (residual floor when uncertain), or null if not applicable.
 */
export function calculateResaleValue(device, formulas, asOf = new Date()) {
  if (!device || !formulas) return null;
  const formula = formulas[formulaKeyFor(device.category)];
  if (!formula) return null;

  const initial = Number(device.purchasePrice);
  if (!Number.isFinite(initial) || initial <= 0) return null;

  const residual = pickResidual(formula, device.name);
  const months   = monthsBetween(device.purchaseDate, asOf);
  if (!Number.isFinite(months) || months < 0) return Math.round(initial);

  if (months >= formula.depreciationMonths) return Math.round(residual);

  // Grace + cliff (phones): full price while STRV doesn't sell it, then an
  // immediate drop to sellableAtPercent, then linear over the REMAINING
  // months. With grace 0 / percent 100 (or the fields absent — laptops)
  // this reduces exactly to the plain line the laptops always used.
  const grace = Number(formula.gracePeriodMonths) || 0;
  const pct   = formula.sellableAtPercent != null ? Number(formula.sellableAtPercent) : 100;
  if (months < grace) return Math.round(initial);

  const start = initial * (pct / 100);
  const span  = formula.depreciationMonths - grace;
  if (span <= 0) return Math.round(residual); // degenerate config: grace swallows the timeline

  const value = start - ((start - residual) * (months - grace)) / span;
  return Math.round(Math.max(residual, Math.min(initial, value)));
}

export function formulaKeyFor(category) {
  // Reads the declarative flag on CATEGORIES rather than re-hardcoding the
  // list: the flag LOOKED load-bearing but nothing consumed it, so adding a
  // resale formula for a new category (e.g. tablet) via config.js would have
  // silently done nothing while this function stayed the real source of truth.
  return CATEGORIES[category]?.resaleFormula || null;
}

/**
 * Pick the residual floor for a device — "fulltext" override matching.
 *
 * An override matches when EVERY word of its `match` appears as a whole word
 * somewhere in the device name — any order, any distance apart. Both sides
 * are normalised first (diacritics stripped, lowercased, punctuation → word
 * breaks), so all of these match the override "macbook air M1":
 *
 *   "Apple MacBook Air 13,3" M1 / 8GB / 256GB SSD / 7x"
 *   "MACBOOK AIR (M1) — šedá"
 *   "Macbook Air M1 13.3"
 *
 * Whole-word matching means "pro" does NOT match "Product", and a "MacBook
 * Air 13" can never take "macbook pro 13" (no word "pro" in its name).
 *
 * PRECEDENCE IS LIST ORDER: the FIRST override that matches wins. Admins
 * reorder rows in Settings (↑/↓), so the rule is explicit and visible rather
 * than inferred — put specific rules ("Macbook Pro M4") above generic ones
 * ("macbook pro").
 */
export function pickResidual(formula, deviceName) {
  if (!formula) return null;
  if (!deviceName || !Array.isArray(formula.modelOverrides)) return formula.residualValue;
  const nameTokens = new Set(tokensOf(deviceName));
  for (const override of formula.modelOverrides) {
    const needle = tokensOf(override.match);
    if (needle.length && needle.every((t) => nameTokens.has(t))) return override.residualValue;
  }
  return formula.residualValue;
}

function tokensOf(s) {
  const n = normaliseForMatch(s);
  return n ? n.split(' ') : [];
}

/**
 * Normalise a string for matching: drop diacritics,
 * lowercase, collapse any non-alphanumeric run to a single space, trim.
 * Same shape as functions/lib/admin-db.js::normaliseName but kept local
 * so the formula module has no Firebase dependency.
 */
function normaliseForMatch(s) {
  return String(s)
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}
